import React, { useEffect, useRef, useState } from 'react'
import { get } from 'helpers/api_helper'
import { initialClubValues, validateClubCreation, clubDays } from './clubCreationForm'
import { clubClock } from './ClubHoursEditor'

const labels = { clubName: 'Club name', businessName: 'Business name', country: 'Country', sports: 'Sports',
  ownerFirstName: 'Owner first name', ownerLastName: 'Owner last name', ownerEmail: 'Owner email', ownerPhone: 'Owner phone',
  contactFirstName: 'Contact first name', contactLastName: 'Contact last name', contactEmail: 'Public contact email', contactPhone: 'Public contact phone',
  website: 'Website', description: 'About the club', directionsInstructions: 'Directions', facebookUrl: 'Facebook URL', instagramUrl: 'Instagram URL', twitterUrl: 'X / Twitter URL' }
const importValues = (source, options) => {
  const values = {}
  const copy = (key, value, max = 200) => { if (typeof value === 'string' && value.trim() && value.trim().length <= max) values[key] = value.trim() }
  for (const key of ['clubName', 'businessName']) copy(key, source[key])
  copy('country', source.location?.country || source.country)
  for (const [prefix, person] of [['owner', source.ownerInfo || source], ['contact', source.contactInfo || {}]]) {
    for (const name of ['firstName', 'lastName', 'email', 'phone']) copy(prefix + name.charAt(0).toUpperCase() + name.slice(1), person[name], name === 'email' ? 254 : 100)
  }
  copy('website', source.contactInfo?.website || source.website, 1000)
  for (const key of ['facebookUrl', 'instagramUrl', 'twitterUrl']) copy(key, source[key], 1000)
  copy('description', source.description, 10000); copy('directionsInstructions', source.directionsInstructions, 5000)
  const sourceSports = source.sports ?? source.sportType
  const sports = Array.isArray(sourceSports) ? sourceSports : [sourceSports].filter(Boolean)
  values.sports = sports.filter(sport => ['tennis', 'padel'].includes(sport) && options.activeSports?.includes(sport))
  if (!values.sports.length) delete values.sports
  for (const sport of ['tennis', 'padel']) {
    const count = source.courtsInfo?.[sport]?.totalCourts
    if (Number.isInteger(count) && count >= 0 && count <= 1000) values[sport + 'Courts'] = String(count)
  }
  for (const day of Object.keys(clubDays)) {
    const archived = Array.isArray(source.openHours) && source.openHours.find(entry =>
      [day.toLowerCase(), clubDays[day].toLowerCase()].includes(entry.day?.toLowerCase()))
    const hours = archived ? { enabled: true, startTime: archived.openHour, endTime: archived.endHour }
      : source.openHours?.[day] || source.openHours?.[clubDays[day].toLowerCase()]
    if (hours?.enabled === false) values[day + 'State'] = 'closed'
    else if (hours?.enabled === true) {
      const start = clubClock(hours, 'start'), end = clubClock(hours, 'end')
      if (start && end && end > start) Object.assign(values, { [day + 'State']: 'open', [day + 'Start']: start, [day + 'End']: end })
    }
  }
  const errors = validateClubCreation({ ...initialClubValues(), ...values }, options)
  for (const key of Object.keys(errors)) delete values[key]
  // A source locality/placeId does not prove that it is a physical club address.
  return values
}
const empty = value => value === '' || value == null || (Array.isArray(value) && !value.length)
const additions = (values, form) => Object.fromEntries(Object.entries(values).filter(([key]) => empty(form.values[key]) && !form.touched[key]))
const label = key => labels[key] || Object.entries(clubDays).reduce((name, [day, dayName]) => name.replace(day, dayName + ' '), key)

export default function ClubCreationSource({ leadId, options, form, locked, onBusy }) {
  const current = useRef(form); current.current = form
  const [loading, setLoading] = useState(Boolean(leadId))
  const [failure, setFailure] = useState('')
  const [retry, setRetry] = useState(0)
  const [archive, setArchive] = useState(null)
  const [review, setReview] = useState(false)
  const [archiveFailure, setArchiveFailure] = useState(false)
  useEffect(() => {
    if (!leadId || !options.eligibleCountries?.length) return undefined
    let active = true
    setLoading(true); onBusy(true); setFailure(''); setArchive(null); setArchiveFailure(false)
    const load = async () => {
      let lead
      try {
        const response = await get('/clubs-leads/' + encodeURIComponent(leadId), { timeout: 10000 })
        if (!response?.data || typeof response.data !== 'object' || !response.data.clubName) throw new Error()
        if (!active) return
        lead = response.data
        const imported = additions(importValues(lead, options), current.current)
        current.current.setValues(values => ({ ...values, ...imported }))
        setLoading(false); onBusy(false)
      } catch {
        if (active) { setLoading(false); setFailure('The club lead could not be loaded. Retry before creating the club.'); onBusy(true) }
        return
      }
      try {
        const params = Object.fromEntries(['clubName', 'email', 'phone'].filter(key => typeof lead[key] === 'string' && lead[key].trim()).map(key => [key, lead[key]]))
        const response = await get('/clubs-archive', { params, timeout: 10000 })
        if (active && response?.data && !Array.isArray(response.data)) setArchive(importValues(response.data, options))
      } catch { if (active) setArchiveFailure(true) }
    }
    load()
    return () => { active = false }
  }, [leadId, options, retry, onBusy])
  if (!leadId) return null
  const suggestions = archive ? additions(archive, form) : {}
  return <div className="alert alert-info">
    {loading ? <p role="status">Loading club lead…</p> : failure ? <p role="alert">{failure}</p>
      : <p>Started from the selected club lead. Verify its details and choose the physical club address.</p>}
    {(failure || archiveFailure) && <><p>{archiveFailure && 'Archive suggestions could not be checked.'}</p>
      <button className="btn btn-light" type="button" disabled={locked} onClick={() => setRetry(value => value + 1)}>Retry lead information</button></>}
    {Object.keys(suggestions).length > 0 && !review && <button type="button" className="btn btn-light" disabled={locked} onClick={() => setReview(true)}>Review archive suggestions</button>}
    {review && <><p>Only the empty, unedited fields listed below will be filled.</p>
      <dl>{Object.entries(suggestions).map(([key, value]) => <React.Fragment key={key}><dt>{label(key)}</dt><dd>{Array.isArray(value) ? value.join(', ') : value}</dd></React.Fragment>)}</dl>
      <button type="button" className="btn btn-primary" disabled={locked || !Object.keys(suggestions).length} onClick={() => {
        form.setValues(values => ({ ...values, ...additions(archive, { ...current.current, values }) })); setReview(false)
      }}>Fill empty fields</button></>}
  </div>
}
